import { api } from './client';

export interface ProjectLinkedNote {
    id: string;
    name: string;
}

export interface ProjectItem {
    id: string;
    name: string;
    description: string;
    paths: string;
    status: string;
    notes: string;
    assets_json: string;
    task_ids: string[];
    note_ids: string[];
    linked_notes?: ProjectLinkedNote[];
    asset_ids: string[];
    tags?: import('../types/entities/actor.entity').TagItem[];
    created_by?: import('../types/entities/actor.entity').Actor | null;
    created_at: string;
    updated_at: string;
}

export interface UpsertProjectPayload {
    name: string;
    description?: string;
    paths?: string;
    status?: string;
    notes?: string;
    assets_json?: string;
    task_ids?: string[];
    note_ids?: string[];
    asset_ids?: string[];
    /** Tag names (category labels such as "zákazník", "úrad-SK"). */
    tags?: string[];
}

interface ProjectResponse {
    status: string;
    data: {
        project: ProjectItem;
    };
}

interface ProjectsCatalogResponse {
    status: string;
    data: {
        projects: ProjectItem[];
    };
}

interface ProjectTasksResponse {
    status: string;
    data: {
        tasks: import('../types/entities/task.entity').Task[];
    };
}

interface ProjectAssetsResponse {
    status: string;
    data: {
        assets: import('../types/entities/external-asset.entity').ExternalAsset[];
    };
}

export const projectsService = {
    getCatalog: async (options?: { limit?: number }): Promise<ProjectItem[]> => {
        const limit = options?.limit;
        const suffix =
            limit != null && limit > 0
                ? `?limit=${encodeURIComponent(String(limit))}`
                : '';
        const response = await api.get<ProjectsCatalogResponse>(`/projects/catalog${suffix}`);
        return response.data.data.projects ?? [];
    },

    getOne: async (id: string): Promise<ProjectItem> => {
        const response = await api.get<ProjectResponse>(`/projects/${encodeURIComponent(id)}`);
        return response.data.data.project;
    },

    create: async (payload: UpsertProjectPayload): Promise<ProjectItem> => {
        const response = await api.post<ProjectResponse>('/projects', payload);
        return response.data.data.project;
    },

    update: async (id: string, payload: UpsertProjectPayload): Promise<ProjectItem> => {
        const response = await api.put<ProjectResponse>(`/projects/${encodeURIComponent(id)}`, payload);
        return response.data.data.project;
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/projects/${encodeURIComponent(id)}`);
    },

    /** Replace only the project's tags. */
    setTags: async (id: string, tags: string[]): Promise<ProjectItem> => {
        const response = await api.patch<ProjectResponse>(`/projects/${encodeURIComponent(id)}`, { tags });
        return response.data.data.project;
    },

    getProjectTasks: async (id: string) => {
        const response = await api.get<ProjectTasksResponse>(`/projects/${encodeURIComponent(id)}/tasks`);
        return response.data.data.tasks ?? [];
    },

    getProjectAssets: async (id: string) => {
        const response = await api.get<ProjectAssetsResponse>(`/projects/${encodeURIComponent(id)}/assets`);
        return response.data.data.assets ?? [];
    },
};
